import type { Schemas } from '@npc/api-client'

import type { TagTone } from '../components/tag'

/**
 * 机器状态那句话用什么颜色。
 *
 * **直接用后端给的 `status_tone`，前端一处都不判。**
 * 上一版是照 `status` 枚举推的，代价很具体：设计稿里「盘偏满」是琥珀色，
 * 但那台机器的 `status` 还是 `online`——**盘满不满和探针在不在线是两件事**，
 * 推不出来，于是它显示成了绿色。契约批次一补上 `status_tone` 之后那段推断就删了。
 *
 * 后端没给的时候退到 neutral，**不回去猜**：猜错的颜色比没有颜色更糟，
 * 一条「盘快满了」被涂成绿的，用户会以为没事。
 *
 * 放在 `app/` 而不是某一屏里，是因为机器列表与项目详情两屏都要用它——
 * 判法只许有一份，两边各写一次必然走偏。
 */
export function statusTone(host: Schemas['HostSummary']): TagTone {
  return host.status_tone ?? 'neutral'
}

/**
 * 父机器的名字，**取不到就返回 null**。
 *
 * `HostRef.name` 在契约上是必填的字符串，而后端那侧取名字的函数
 * （`hostName()`）**在名字和地址都没有时会返回空串**——流 A 把它从
 * 「编一句『另一台机器』」改成「出数据不出句子」之后就有这一档了。
 *
 * 空串塞进「住在「{parent_name}」上」会渲染成**「住在「」上」**——
 * 一对空引号，比不显示更糟：它看着像个渲染 bug，而且一个字的信息都没有。
 *
 * 所以这里把空串收敛成 null，由调用点决定「那一行整个不画」。
 * 同时打一条诊断——**界面上不能说的，控制台要说**：
 * 一个没有名字的父机器是后端那侧的状态，我们至少要知道它出现过。
 */
export function parentName(parent: { id: string; name: string } | null | undefined): string | null {
  if (!parent) return null
  const name = parent.name.trim()
  if (name) return name
  console.error(`[npc] 这台机器的父机器没有名字（id=${parent.id}）——那一行不画了。`)
  return null
}
